import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  PlayArrow,
  Stop,
  Visibility,
  Edit,
  Campaign,
  Save,
  Cancel,
  Schedule,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCampaigns,
  createCampaign,
  executeCampaign
} from '../../redux/actions/communicationActions';

const CampaignManager = () => {
  const dispatch = useDispatch();
  const { campaigns, loading, error } = useSelector((state) => state.communication || {});
  
  const [openDialog, setOpenDialog] = useState(false);
  const [campaignData, setCampaignData] = useState({
    name: '',
    description: '',
    type: 'offer',
    email_template_id: '',
    whatsapp_template_id: '',
    target_criteria: {},
    scheduled_at: ''
  });

  useEffect(() => {
    dispatch(getCampaigns());
  }, [dispatch]);

  const handleOpenDialog = () => {
    setCampaignData({
      name: '',
      description: '',
      type: 'offer',
      email_template_id: '',
      whatsapp_template_id: '',
      target_criteria: {},
      scheduled_at: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCampaignData({
      name: '',
      description: '',
      type: 'offer',
      email_template_id: '',
      whatsapp_template_id: '',
      target_criteria: {},
      scheduled_at: ''
    });
  };

  const handleSaveCampaign = async () => {
    const result = await dispatch(createCampaign(campaignData));
    if (result.success) {
      handleCloseDialog();
      dispatch(getCampaigns());
    }
  };

  const handleExecuteCampaign = async (campaignId) => {
    if (window.confirm('Are you sure you want to execute this campaign? This will send communications to all target customers.')) {
      const result = await dispatch(executeCampaign(campaignId));
      if (result.success) {
        dispatch(getCampaigns());
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'scheduled': return 'info';
      case 'running': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'offer': return 'success';
      case 'birthday': return 'warning';
      case 'announcement': return 'info';
      case 'newsletter': return 'primary';
      default: return 'default';
    }
  };

  const campaignTypes = [
    { value: 'offer', label: 'Offer' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'announcement', label: 'Announcement' },
    { value: 'newsletter', label: 'Newsletter' }
  ];

  const totalCampaigns = campaigns.length;
  const activeCampaigns = campaigns.filter(c => c.status === 'running').length;
  const completedCampaigns = campaigns.filter(c => c.status === 'completed').length;
  const scheduledCampaigns = campaigns.filter(c => c.status === 'scheduled').length;

  if (loading && campaigns.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Communication Campaigns
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage marketing campaigns to send bulk communications
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenDialog}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #d97ae8 0%, #e0455a 100%)',
            }
          }}
        >
          Create Campaign
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Campaign Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: 2,
            borderRadius: 2,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Campaigns
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {totalCampaigns}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  <Campaign />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: 2,
            borderRadius: 2,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Campaigns
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {activeCampaigns}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48 }}>
                  <PlayArrow />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: 2,
            borderRadius: 2,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Completed
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {completedCampaigns}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                  <Stop />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: 2,
            borderRadius: 2,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Scheduled
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {scheduledCampaigns}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 48, height: 48 }}>
                  <Schedule />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Campaigns Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Campaign Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Recipients</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Emails Sent</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>WhatsApp Sent</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No campaigns found. Create your first campaign to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => (
                <TableRow 
                  key={campaign.id}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:last-child td': { border: 0 }
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {campaign.name}
                      </Typography>
                      {campaign.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                          {campaign.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={campaign.type}
                      color={getTypeColor(campaign.type)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={campaign.status}
                      color={getStatusColor(campaign.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {campaign.total_recipients || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="primary.main" fontWeight="medium">
                      {campaign.emails_sent || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="success.main" fontWeight="medium">
                      {campaign.whatsapp_sent || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(campaign.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {campaign.status === 'draft' && (
                        <Tooltip title="Execute Campaign">
                          <IconButton
                            size="small"
                            onClick={() => handleExecuteCampaign(campaign.id)}
                            color="primary"
                          >
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Campaign Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Campaign />
          <Typography variant="h6" fontWeight="bold">
            Create Communication Campaign
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Campaign Name"
              value={campaignData.name}
              onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Diwali Special Offer 2024"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Campaign color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={campaignData.description}
              onChange={(e) => setCampaignData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of the campaign"
            />

            <FormControl fullWidth>
              <InputLabel>Campaign Type</InputLabel>
              <Select
                value={campaignData.type}
                onChange={(e) => setCampaignData(prev => ({ ...prev, type: e.target.value }))}
                label="Campaign Type"
              >
                {campaignTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="datetime-local"
              label="Schedule Campaign (Optional)"
              value={campaignData.scheduled_at}
              onChange={(e) => setCampaignData(prev => ({ ...prev, scheduled_at: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to execute immediately after creation"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Schedule color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Note:</strong> You can select email and WhatsApp templates after creating the campaign. 
                The campaign will target all customers by default unless you specify target criteria.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            startIcon={<Cancel />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCampaign}
            variant="contained"
            disabled={!campaignData.name}
            startIcon={<Save />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              px: 3
            }}
          >
            Create Campaign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignManager;
